import classNames from "classnames";
import { ReactNode } from "react";
import { CreatedEvent } from "../../redux/features/event/event.types";
import { Icon } from "../icon/icon";
import { Pill } from "../pill";
import { BgWrapper } from "../wrapper/bgWrapper";
import { EventImage } from "./eventImage";

export const EventCard = ({
  image,
  name,
  location,
  startDate,
  endDate,
  startTime,
  endTime,
  noTickets,
  totalGuests,
  isOrganizer,
  event,
  children,
  className,
  imageDimension = 300,
  clickable = false,
  onClick = () => {},
}: {
  image: string;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  noTickets?: number;
  totalGuests?: number;
  isOrganizer: boolean;
  event?: CreatedEvent;
  children?: ReactNode;
  className?: string;
  imageDimension?: number;
  clickable?: boolean;
  onClick?: () => void;
}) => {
  return (
    <BgWrapper
      className={classNames(
        `flex flex-col gap-8 md:flex-row md:flex-wrap xl:items-center`,
        {
          "!cursor-pointer": clickable,
        },
        className,
      )}
      onClick={onClick}
    >
      <EventImage image={image} dimension={imageDimension} className="" />

      <div
        className={classNames(
          "flex w-full flex-col justify-center gap-4 md:min-w-[350px] md:flex-1 xl:!min-h-full xl:w-fit",
          {
            "md:min-h-[200px]": imageDimension === 200,
            "md:min-h-[300px]": imageDimension === 300,
            "md:min-h-[400px]": imageDimension === 400,
          },
        )}
      >
        <div className="flex items-center gap-2">
          <Pill
            className=""
            text={isOrganizer ? "Organizer" : "Guest"}
            variation={isOrganizer ? "purple" : "grey"}
          />

          {event?.status === "draft" && (
            <Pill className="" text={"Draft"} variation={"grey"} />
          )}
        </div>

        <p className="text-32 font-fraunces font-medium">{name}</p>

        <div className="flex flex-col gap-[10px] text-sm text-grey-900">
          <p className="flex items-center gap-2">
            <Icon
              svg="map-pin"
              width={20}
              height={20}
              className="min-w-fit text-grey-500"
            />
            {location}
          </p>

          <p className="flex items-center gap-2">
            <Icon
              svg="calendar"
              width={20}
              height={20}
              className="min-w-fit text-grey-500"
            />
            {startDate} {startDate === endDate ? "" : `- ${endDate}`}
          </p>

          <p className="flex items-center gap-2">
            <Icon
              svg="clock"
              width={20}
              height={20}
              className="min-w-fit text-grey-500"
            />
            {startTime} - {endTime}
          </p>

          {noTickets !== undefined && !isOrganizer ? (
            <p className="flex items-center gap-2">
              <Icon
                svg="ticket"
                width={20}
                height={20}
                className="min-w-fit text-grey-500"
              />
              {noTickets} {noTickets === 1 ? "Ticket" : "Tickets"}
            </p>
          ) : undefined}

          {isOrganizer ? (
            <p className="flex items-center gap-2">
              <Icon
                svg="users"
                width={20}
                height={20}
                className="min-w-fit text-grey-500"
              />
              {(totalGuests ?? 0) > 0 ? totalGuests : "No"}{" "}
              {(totalGuests ?? 0) === 1 ? "Guest" : "Guests"}
            </p>
          ) : undefined}
        </div>

        {children}
      </div>
    </BgWrapper>
  );
};

export const EventCardSkeleton = ({
  className,
  imageDimension = 300,
  showExtraDetails = false,
}: {
  className?: string;
  imageDimension?: number;
  showExtraDetails?: boolean;
}) => {
  return (
    <BgWrapper
      className={`flex flex-col gap-8 md:flex-row md:items-center ${className}`}
    >
      <div
        className={classNames(
          "w-full animate-pulse rounded-[14px] bg-grey-50",
          {
            "h-[300px] md:max-w-[300px]": imageDimension === 300,
            "h-[300px] md:h-[400px] md:max-w-[400px]": imageDimension === 400,
          },
        )}
      />

      <div className="flex h-full flex-1 flex-col gap-4">
        <div className="h-6 w-20 animate-pulse rounded-xl bg-grey-50" />

        <div className="h-8 w-48 animate-pulse rounded-xl bg-grey-50" />

        <div className="flex flex-col gap-[10px]">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              className="flex items-center gap-2"
              key={`skeletion-sub-details-${index}`}
            >
              <div className="h-5 w-5 animate-pulse rounded-xl bg-grey-50" />
              <div className="h-4 w-32 animate-pulse rounded-xl bg-grey-50" />
            </div>
          ))}
        </div>

        {showExtraDetails && (
          <div className="flex flex-col gap-2">
            <div className="h-8 w-full animate-pulse rounded-xl bg-grey-50" />
            <div className="h-8 w-11/12 animate-pulse rounded-xl bg-grey-50" />
            <div className="h-8 w-10/12 animate-pulse rounded-xl bg-grey-50" />
          </div>
        )}
      </div>
    </BgWrapper>
  );
};
